import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import { Popover, Stack, Typography } from '@mui/material';
import { MAX_CHILD_AGE } from '../utils/childrenAgesParam';

type CounterField = 'adults' | 'children' | 'rooms';

interface HotelPassengerSelectorIcons {
  adult: React.ElementType;
  child: React.ElementType;
  room: React.ElementType;
}

interface HotelPassengerSelectorProps {
  adults: number;
  childrenCount: number;
  rooms: number;
  /** Age of each child (0–17); `undefined` while not selected yet. */
  childrenAges?: (number | undefined)[];
  onAdultsChange: (quantity: number) => void;
  onChildrenChange: (quantity: number) => void;
  onChildrenAgesChange?: (ages: (number | undefined)[]) => void;
  onRoomsChange: (quantity: number) => void;
  hasError?: boolean;
  describedBy?: string;
  inputRef?: (node: HTMLButtonElement | null) => void;
  icons?: HotelPassengerSelectorIcons;
}

const counterMinimums: Record<CounterField, number> = {
  adults: 1,
  children: 0,
  rooms: 1,
};

const defaultIcons: HotelPassengerSelectorIcons = {
  adult: PersonOutlineIcon,
  child: ChildCareIcon,
  room: HotelIcon,
};

const childAgeOptions = Array.from({ length: MAX_CHILD_AGE + 1 }, (_, age) => age);

const countersConfig: Array<{ field: CounterField; decrementKey: string; incrementKey: string }> = [
  { field: 'adults', decrementKey: 'decrementAdults', incrementKey: 'incrementAdults' },
  { field: 'children', decrementKey: 'decrementChildren', incrementKey: 'incrementChildren' },
  { field: 'rooms', decrementKey: 'decrementRooms', incrementKey: 'incrementRooms' },
];

export function HotelPassengerSelector({
  adults,
  childrenCount,
  rooms,
  childrenAges = [],
  onAdultsChange,
  onChildrenChange,
  onChildrenAgesChange,
  onRoomsChange,
  hasError = false,
  describedBy,
  inputRef,
  icons = defaultIcons,
}: HotelPassengerSelectorProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'pasajeros-popover' : undefined;

  const values: Record<CounterField, number> = { adults, children: childrenCount, rooms };
  const setters: Record<CounterField, (quantity: number) => void> = {
    adults: onAdultsChange,
    children: onChildrenChange,
    rooms: onRoomsChange,
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCounterChange = (field: CounterField, quantity: number) => {
    const nextQuantity = Math.max(counterMinimums[field], quantity);
    setters[field](nextQuantity);
  };

  const handleChildAgeChange = (index: number, rawValue: string) => {
    const nextAges = Array.from({ length: childrenCount }, (_, i) => childrenAges[i]);
    nextAges[index] = rawValue === '' ? undefined : Number(rawValue);
    onChildrenAgesChange?.(nextAges);
  };

  const renderChildAges = () => {
    if (childrenCount <= 0 || !onChildrenAgesChange) return null;

    return (
      <div className='mt-3 border-t border-gray-200 pt-3'>
        <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 1 }}>
          {t('hotels.form.childrenAgesHint')}
        </Typography>
        <div className='grid grid-cols-2 gap-2'>
          {Array.from({ length: childrenCount }, (_, index) => {
            const selectId = `hotel-child-age-${index}`;
            const value = childrenAges[index];
            return (
              <label
                className='flex flex-col gap-1 text-xs text-gray-600'
                htmlFor={selectId}
                key={selectId}
              >
                {t('hotels.form.childAgeLabel', { number: index + 1 })}
                <select
                  className={`rounded-md border px-2 py-1.5 text-sm text-gray-900 ${
                    hasError && value === undefined ? 'border-red-400' : 'border-gray-300'
                  }`}
                  id={selectId}
                  onChange={(event) => handleChildAgeChange(index, event.target.value)}
                  value={value === undefined ? '' : String(value)}
                >
                  <option disabled value=''>
                    {t('hotels.form.childAgePlaceholder')}
                  </option>
                  {childAgeOptions.map((age) => (
                    <option key={age} value={age}>
                      {t('hotels.form.childAgeOption', { count: age })}
                    </option>
                  ))}
                </select>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPreviewItem = (field: CounterField, Icon: React.ElementType, iconLabel: string) => (
    <span className='flex flex-1 items-center justify-center gap-1'>
      <span aria-label={iconLabel} role='img'>
        <Icon aria-hidden='true' focusable='false' fontSize='small' style={{ fill: '#6b7280' }} />
      </span>
      <span>{values[field]}</span>
    </span>
  );

  const renderCounter = ({
    field,
    decrementKey,
    incrementKey,
  }: (typeof countersConfig)[number]) => {
    const value = values[field];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Stack direction='column'>
          <Typography>{t(`hotels.form.${field}`)}</Typography>
        </Stack>
        <Stack alignItems='center' direction='row' justifyContent='space-between'>
          <button
            aria-label={t(`hotels.form.${decrementKey}`)}
            disabled={value <= counterMinimums[field]}
            onClick={() => handleCounterChange(field, value - 1)}
            style={{
              backgroundColor: '#4f4f4f',
              border: 0,
              borderBottomLeftRadius: 8,
              borderTopLeftRadius: 8,
              color: 'white',
              padding: 2,
            }}
            type='button'
          >
            <RemoveIcon height={30} style={{ fill: 'white' }} width={30} />
          </button>
          <span
            aria-live='polite'
            style={{
              height: 30,
              width: 30,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {value}
          </span>
          <button
            aria-label={t(`hotels.form.${incrementKey}`)}
            onClick={() => handleCounterChange(field, value + 1)}
            style={{
              backgroundColor: '#4f4f4f',
              border: 0,
              borderBottomRightRadius: 8,
              borderTopRightRadius: 8,
              color: 'white',
              padding: 2,
            }}
            type='button'
          >
            <AddIcon style={{ fill: 'white' }} />
          </button>
        </Stack>
      </div>
    );
  };

  return (
    <div className='pasajeros-input-container'>
      <button
        aria-describedby={describedBy ?? id}
        aria-expanded={open}
        aria-haspopup='dialog'
        aria-label={t('hotels.form.guestsAndRooms')}
        className={`pasajeros-box-container ${hasError ? 'turistasqty-error' : ''}`}
        onClick={handleClick}
        ref={inputRef}
        style={{
          width: '100%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '48px',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          transform: 'none',
        }}
        type='button'
      >
        {renderPreviewItem('adults', icons.adult, t('hotels.form.adultIcon'))}
        <span aria-hidden='true' className='shrink-0'>
          {' '}
          ·{' '}
        </span>
        {renderPreviewItem('children', icons.child, t('hotels.form.childIcon'))}
        <span aria-hidden='true' className='shrink-0'>
          {' '}
          ·{' '}
        </span>
        {renderPreviewItem('rooms', icons.room, t('hotels.form.roomIcon'))}
      </button>

      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <div
          aria-label={t('hotels.form.guestsAndRooms')}
          className='pasajeros-popup-container'
          role='dialog'
        >
          <div
            aria-label={t('hotels.form.guestsAndRooms')}
            className='contador-pasajeros-container'
            role='list'
            style={{ flexDirection: 'column', marginTop: 0 }}
          >
            {countersConfig.map((counter) => (
              <div key={counter.field} role='listitem'>
                {renderCounter(counter)}
              </div>
            ))}
          </div>
          {renderChildAges()}
        </div>
      </Popover>
    </div>
  );
}
